import { useEffect, useState } from "react";
import { Clock, MapPin, Flag } from "lucide-react";

function formatHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/**
 * Live chronometer for hourly/day rentals.
 *
 * - While the trip is `in_progress`, ticks every second from `startedAt`.
 * - Once the trip is `completed`, freezes on the stored duration and shows km traveled.
 * - Renders nothing if the trip hasn't started (no OTP verified yet).
 *
 * Props:
 *   booking: the booking object (must contain otp_verified, status,
 *            trip_started_at | otp_verified_at, trip_duration_seconds,
 *            trip_distance_km, rental_mode)
 *   compact: smaller padding when shown in a card list
 *   role: "driver" | "passenger" (small label tweak)
 */
export default function TripChronometer({ booking, compact = false, role = "passenger" }) {
  const b = booking || {};
  const mode = b.rental_mode || "";
  const isHourlyOrDay = mode === "hourly" || mode === "city_tour" || mode === "out_of_city";
  const started = b.trip_started_at || b.otp_verified_at;
  // Iter 75: the driver can "Terminer la course" before payment. When
  // that happens, `trip_finished_at` is set and the chrono must freeze
  // even though `status` is still `in_progress`.
  const finishedAt = b.trip_finished_at;
  const isFrozen = b.status === "in_progress" && b.otp_verified && !!finishedAt;
  const isInProgress = b.status === "in_progress" && b.otp_verified && !!started && !isFrozen;
  const isCompleted = b.status === "completed" && (b.trip_duration_seconds || started);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isInProgress) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isInProgress]);

  if (!isHourlyOrDay) return null;
  if (!isInProgress && !isCompleted && !isFrozen) return null;

  let elapsed = 0;
  if (isCompleted && b.trip_duration_seconds) {
    elapsed = b.trip_duration_seconds;
  } else if (isFrozen) {
    // Prefer server-stored duration; fall back to computed diff.
    elapsed = Number(b.trip_duration_seconds || 0);
    if (!elapsed && started && finishedAt) {
      try { elapsed = (new Date(finishedAt).getTime() - new Date(started).getTime()) / 1000; }
      catch { elapsed = 0; }
    }
  } else if (started) {
    try {
      elapsed = (now - new Date(started).getTime()) / 1000;
    } catch {
      elapsed = 0;
    }
  }

  const km = Math.max(0, Number(b.trip_distance_km || 0));
  const label = isInProgress
    ? role === "driver" ? "Course en cours" : "Trajet en cours"
    : isFrozen
      ? "Chrono figé — en attente du paiement"
      : "Course terminée";

  return (
    <div
      className={`mt-2 rounded-lg border ${
        isInProgress ? "border-[#10B981]/40 bg-[#10B981]/5" : "border-[#0A2540]/20 bg-[#F8FAFC]"
      } ${compact ? "p-2" : "p-3"}`}
      data-testid={`trip-chronometer-${b.id}`}
      role="status"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
              isInProgress ? "bg-[#10B981] text-white animate-pulse" : "bg-[#0A2540] text-white"
            }`}
          >
            {isInProgress ? <Clock className="w-3.5 h-3.5" /> : <Flag className="w-3.5 h-3.5" />}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-[#64748B] leading-none">
              {label}
            </p>
            <p
              className="text-base font-bold text-[#0A2540] tabular-nums leading-tight"
              style={{ fontFamily: "Outfit" }}
              data-testid={`trip-chronometer-time-${b.id}`}
            >
              {formatHMS(elapsed)}
            </p>
          </div>
        </div>
        {isCompleted && (
          <div className="flex items-center gap-1.5 text-right shrink-0">
            <MapPin className="w-3.5 h-3.5 text-[#0A2540]" />
            <div>
              <p className="text-[10px] uppercase font-semibold tracking-wider text-[#64748B] leading-none">
                Distance
              </p>
              <p
                className="text-base font-bold text-[#0A2540] tabular-nums leading-tight"
                style={{ fontFamily: "Outfit" }}
                data-testid={`trip-distance-${b.id}`}
              >
                {km.toFixed(2)} km
              </p>
            </div>
          </div>
        )}
        {isInProgress && km > 0 && (
          <div className="flex items-center gap-1.5 text-right shrink-0">
            <MapPin className="w-3.5 h-3.5 text-[#10B981]" />
            <p
              className="text-sm font-bold text-[#0A2540] tabular-nums"
              data-testid={`trip-live-distance-${b.id}`}
            >
              {km.toFixed(2)} km
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
